import React, { useState, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { library } from '@fortawesome/fontawesome-svg-core';
import { API_URL } from '../config';
import { useAmazonSalesUpload } from './useAmazonSalesUpload';
import AmazonSalesUploadPopup from './AmazonSalesUploadPopup';
import YoyAwdUploadPopup from './YoyAwdUploadPopup';
import { useYoyAwdUpload } from './useYoyAwdUpload';
import { useWalmartUpload } from './useWalmartUpload';
import WalmartUploadPopup from './WalmartUploadPopup';
import { useEtsyUpload } from './useEtsyUpload';
import EtsyUploadPopup from './EtsyUploadPopup';
import { useShopifyUpload } from './useShopifyUpload';
import ShopifyUploadPopup from './ShopifyUploadPopup';
import useSeasonalityCoefficients from './useSeasonalityCoefficients';
import SeasonalityCoefficientsPopup from './SeasonalityCoefficientsPopup';
import { useWalmartStockUpload } from './useWalmartStockUpload';
import WalmartStockUploadPopup from './WalmartStockUploadPopup';
import { useSelleriseUpload } from './useSelleriseUpload';
import SelleriseUploadPopup from './SelleriseUploadPopup';
import { 
  fas, 
  faUpload, 
  faFilter, 
  faAngleUp, 
  faAngleDown,
  faArrowsRotate,
  faSpinner,
  faCheckCircle,
  faExclamationCircle,
  faFile,
  faFolderOpen,
  faDatabase,
  faTimes,
  faInfoCircle,
  faLightbulb,
  faMagic,
  faWeightHanging,
  faBarcode,
  faTruckLoading,
  faWarehouse,
  faTable,
  faEllipsisH,
  faExclamationTriangle,
  faSearch,
  faClock,
  faSync,
  faBox,
  faCheckCircle as faCheckCircleSolid
} from '@fortawesome/free-solid-svg-icons';
// Добавляем иконки в библиотеку
library.add(
  fas,
  faUpload,
  faFilter,
  faAngleUp,
  faAngleDown,
  faArrowsRotate,
  faSpinner,
  faCheckCircle,
  faExclamationCircle,
  faFile,
  faFolderOpen,
  faDatabase,
  faTimes,
  faInfoCircle,
  faLightbulb,
  faMagic,
  faWeightHanging,
  faBarcode,
  faTruckLoading,
  faWarehouse,
  faTable,
  faEllipsisH,
  faExclamationTriangle,
  faSearch,
  faClock,
  faSync,
  faBox,
  faCheckCircleSolid
);


function YoyAdminPanel({
  // Состояния поиска и фильтров
  searchQuery,
  setSearchQuery,

  // Состояния для загрузки данных
  showStockUploadPopup,
  setShowStockUploadPopup,

  // Состояния для загрузки стока
  selectedStockFile,
  setSelectedStockFile,
  parsedStockData,
  setParsedStockData,
  uploadStockStatus,
  setUploadStockStatus,
  stockFileInputRef,
  handleStockFileUpload,
  handleStockUploadSubmit,
  
  // Фильтры по брендам и нишам
  brandsData,
  nichesData,
  selectedBrands,
  setSelectedBrands,
  selectedNiches,
  setSelectedNiches,
  selectedProductTypes,
  setSelectedProductTypes,
  
  // Управление отображением колонок
  visibleColumns,
  setVisibleColumns,
  showAllPreviousYears,
  setShowAllPreviousYears,
  
  // Управление количеством недель
  historicalWeeksCount,
  setHistoricalWeeksCount,
  forecastWeeksCount,
  setForecastWeeksCount,
  customHistoricalWeeks,
  setCustomHistoricalWeeks,
  customForecastWeeks,
  setCustomForecastWeeks,
  
  // Функции
  loadProducts,
  loadSalesData,
  applyStandardHistoricalWeeks,
  applyCustomHistoricalWeeks,
  applyStandardForecastWeeks,
  applyCustomForecastWeeks,
  toggleColumn,
  toggleAllPreviousYears,
  handleBrandFilterChange,
  handleNicheFilterChange,
  handleProductTypeFilterChange,
  clearAllFilters,
  clearBrandFilter,
  clearNicheFilter,
  clearProductTypeFilter,
  
  // Stock Balance Row toggle
  showStockBalanceRow,
  toggleStockBalanceRow,

  // Agentic (AI forecast) row toggle
  showAgenticRow,
  toggleAgenticRow,
  onOpenAiHistory,

  // Данные
  productsData,
  filteredProducts,
  overallLoading,

  // Marketplace config — needed for AmazonSalesUploadPopup
  marketplaceConfig,

  // Median weeks for flag calculation
  medianWeeksCount,
  setMedianWeeksCount,

  // View mode ('weekly' | 'monthly')
  viewMode,

  // Month settings
  historicalMonthsCount,
  setHistoricalMonthsCount,
  forecastMonthsCount,
  setForecastMonthsCount,
  medianMonthsCount,
  setMedianMonthsCount,
}) {

  // Локальное состояние тулбара (31.08.2026): раскрытие фильтров раньше жило
  // в YOYForecastPage — каждый клик перерисовывал всю страницу с гигантской
  // таблицей (тумблер лагал). Никому кроме панели это состояние не нужно.
  const [showFilters, setShowFilters] = useState(false);

  const walmartStock = useWalmartStockUpload(loadProducts);

  const amazonSales = useAmazonSalesUpload(
    () => loadSalesData(),
    () => loadSalesData() // loadMonthlySalesData не передаётся в AdminPanel — loadSalesData перезагрузит всё
  );

  // Используем хук для AWD
  const {
    showAwdUploadPopup,
    setShowAwdUploadPopup,
    selectedAwdFile,
    setSelectedAwdFile,
    parsedAwdData,
    setParsedAwdData,
    uploadAwdStatus,
    setUploadAwdStatus,
    awdFileInputRef,
    handleAwdFileUpload,
    handleAwdUploadSubmit,
    closeAwdUploadPopup,
  } = useYoyAwdUpload(loadProducts);

  // Используем отдельный хук для Walmart
  const {
    showWalmartUploadPopup,
    setShowWalmartUploadPopup,
    selectedWalmartFile,
    setSelectedWalmartFile,
    parsedWalmartData,
    setParsedWalmartData,
    uploadWalmartStatus,
    setUploadWalmartStatus,
    uploadWalmartWeek,
    setUploadWalmartWeek,
    uploadWalmartMonth,
    setUploadWalmartMonth,
    uploadWalmartYear,
    setUploadWalmartYear,
    walmartPeriodMode,
    setWalmartPeriodMode,
    walmartFileInputRef,
    handleWalmartFileUpload,
    handleWalmartUploadSubmit,
    closeWalmartUploadPopup
  } = useWalmartUpload(loadProducts);

  // Используем отдельный хук для Etsy
  const {
    showEtsyUploadPopup,
    setShowEtsyUploadPopup,
    selectedEtsyFile,
    setSelectedEtsyFile,
    parsedEtsyData,
    setParsedEtsyData,
    uploadEtsyStatus,
    setUploadEtsyStatus,
    uploadEtsyWeek,
    setUploadEtsyWeek,
    uploadEtsyMonth,
    setUploadEtsyMonth,
    uploadEtsyYear,
    setUploadEtsyYear,
    etsyPeriodMode,
    setEtsyPeriodMode,
    etsyFileInputRef,
    handleEtsyFileUpload,
    handleEtsyUploadSubmit,
    closeEtsyUploadPopup
  } = useEtsyUpload(loadProducts);
  // Используем отдельный хук для Shopify
  const {
    showShopifyUploadPopup,
    setShowShopifyUploadPopup,
    uploadShopifyWeek,
    setUploadShopifyWeek,
    uploadShopifyMonth,
    setUploadShopifyMonth,
    uploadShopifyYear,
    setUploadShopifyYear,
    howManyWeeks,
    setHowManyWeeks,
    uploadMode,
    setUploadMode,
    shopifyPeriodMode,
    setShopifyPeriodMode,
    parsedShopifyData,
    setParsedShopifyData,
    uploadShopifyStatus,
    setUploadShopifyStatus,
    isLoadingPreview,
    isLoadingUpload,
    loadShopifyPreview,
    handleShopifyUploadSubmit,
    closeShopifyUploadPopup
  } = useShopifyUpload(loadProducts);

  const seasonality = useSeasonalityCoefficients(marketplaceConfig?.id || 'amazon');
  
  // Функция для закрытия попапа загрузки
  const sellerise = useSelleriseUpload(loadSalesData);
  // Функция для закрытия попапа загрузки стока
  const closeStockUploadPopup = () => {
    setShowStockUploadPopup(false);
    setSelectedStockFile(null);
    setParsedStockData([]);
    setUploadStockStatus(null);
    if (stockFileInputRef.current) {
      stockFileInputRef.current.value = '';
    }
  };

  const handleStandardHistoricalWeeks = async (weeks) => {
    await applyStandardHistoricalWeeks(weeks);
  };

  const handleCustomHistoricalWeeks = async () => {
    await applyCustomHistoricalWeeks(customHistoricalWeeks);
  };

  
  return (
    <div className="admin-panel">
      <div className="admin-controls">
        {/* Поиск слева */}
        <div className="search-section">
          <div className="search-container">
            <input
              type="text"
              placeholder="Search by product name or ASIN..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="search-input"
            />
            {searchQuery && (
              <button 
                className="clear-search-btn"
                onClick={() => setSearchQuery('')}
              >
                ✕
              </button>
            )}
          </div>
          {searchQuery && (
            <div className="search-stats">
              Found {filteredProducts.length} product{filteredProducts.length !== 1 ? 's' : ''}
            </div>
          )}
        </div>
        
        {/* Кнопки фильтров и загрузки справа */}
        <div className="filters-section" style={{ gap: '10px', position: 'relative' }}>
          {/* Кнопка Upload Data скрыта полностью 01.09.2026: продажи всех
              4 маркетплейсов и сток (SP-API + Walmart WFS incl. inbound)
              тянутся из API по крону — ручным загрузкам не осталось работы.
              Попапы загрузок оставлены в коде как аварийный фолбэк
              (вернуть кнопку — восстановить блок из git-истории). */}

          <button
            className="seasonality-btn"
            onClick={seasonality.openPopup}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              backgroundColor: '#6f42c1', // purple
              color: 'white',
              border: 'none',
              padding: '10px 16px',
              borderRadius: '4px',
              fontSize: '14px',
              cursor: 'pointer',
              transition: 'all 0.2s',
              fontWeight: '500',
              whiteSpace: 'nowrap'
            }}
          >
            <FontAwesomeIcon icon="magic" style={{ color: 'white' }} />
            Edit Seasonality
          </button>

          <button 
            className="refresh-btn"
            onClick={() => {
              loadProducts();
              loadSalesData();
            }}
            disabled={overallLoading}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              backgroundColor: '#28a745',
              color: 'white',
              border: 'none',
              padding: '10px 16px',
              borderRadius: '4px',
              fontSize: '14px',
              cursor: 'pointer',
              transition: 'all 0.2s',
              fontWeight: '500',
              whiteSpace: 'nowrap',
              opacity: overallLoading ? 0.6 : 1
            }}
          >
            <FontAwesomeIcon icon="arrows-rotate" style={{ color: 'white' }} />
            {overallLoading ? 'Loading...' : 'Refresh All Data'}
          </button>

          <button 
            className={`filters-toggle-btn ${showFilters ? 'active' : ''}`}
            onClick={() => setShowFilters(!showFilters)}
          >
            <FontAwesomeIcon icon="filter" style={{ color: 'white' }} />
            Display Filters
            <FontAwesomeIcon 
              icon={showFilters ? "angle-up" : "angle-down"} 
              style={{ marginLeft: '5px', color: 'white' }} 
            />
          </button>
        </div>
      </div>

      <AmazonSalesUploadPopup
        showPopup={amazonSales.showPopup}
        closePopup={amazonSales.closePopup}
        periodMode={amazonSales.periodMode}
        handlePeriodModeChange={amazonSales.handlePeriodModeChange}
        uploadWeek={amazonSales.uploadWeek}
        setUploadWeek={amazonSales.setUploadWeek}
        uploadMonth={amazonSales.uploadMonth}
        setUploadMonth={amazonSales.setUploadMonth}
        uploadYear={amazonSales.uploadYear}
        setUploadYear={amazonSales.setUploadYear}
        selectedFile={amazonSales.selectedFile}
        parsedData={amazonSales.parsedData}
        uploadStatus={amazonSales.uploadStatus}
        fileInputRef={amazonSales.fileInputRef}
        handleFileUpload={amazonSales.handleFileUpload}
        handleUploadSubmit={amazonSales.handleUploadSubmit}
        marketplaceConfig={marketplaceConfig}
      />

      <YoyAwdUploadPopup
        showAwdUploadPopup={showAwdUploadPopup}
        setShowAwdUploadPopup={setShowAwdUploadPopup}
        awdFileInputRef={awdFileInputRef}
        selectedAwdFile={selectedAwdFile}
        setSelectedAwdFile={setSelectedAwdFile}
        parsedAwdData={parsedAwdData}
        setParsedAwdData={setParsedAwdData}
        uploadAwdStatus={uploadAwdStatus}
        setUploadAwdStatus={setUploadAwdStatus}
        handleAwdFileUpload={handleAwdFileUpload}
        handleAwdUploadSubmit={handleAwdUploadSubmit}
      />

      <SelleriseUploadPopup
        showPopup={sellerise.showPopup}
        closePopup={sellerise.closePopup}

        mode={sellerise.mode}
        onModeChange={sellerise.handleModeChange}

        // Weekly
        startDate={sellerise.startDate}
        setStartDate={sellerise.setStartDate}
        endDate={sellerise.endDate}
        setEndDate={sellerise.setEndDate}

        // Monthly
        selectedYear={sellerise.selectedYear}
        setSelectedYear={sellerise.setSelectedYear}
        selectedMonths={sellerise.selectedMonths}
        toggleMonth={sellerise.toggleMonth}
        selectAllMonths={sellerise.selectAllMonths}
        clearAllMonths={sellerise.clearAllMonths}
        monthNames={sellerise.monthNames}

        // Shared
        scrapedRows={sellerise.scrapedRows}
        scrapeStatus={sellerise.scrapeStatus}
        uploadStatus={sellerise.uploadStatus}
        uploadErrors={sellerise.uploadErrors}
        handleFetch={sellerise.handleFetch}
        handleUpload={sellerise.handleUpload}
      />

      <WalmartUploadPopup
        showWalmartUploadPopup={showWalmartUploadPopup}
        setShowWalmartUploadPopup={setShowWalmartUploadPopup}
        walmartFileInputRef={walmartFileInputRef}
        selectedWalmartFile={selectedWalmartFile}
        setSelectedWalmartFile={setSelectedWalmartFile}
        parsedWalmartData={parsedWalmartData}
        setParsedWalmartData={setParsedWalmartData}
        uploadWalmartStatus={uploadWalmartStatus}
        setUploadWalmartStatus={setUploadWalmartStatus}
        uploadWalmartWeek={uploadWalmartWeek}
        setUploadWalmartWeek={setUploadWalmartWeek}
        uploadWalmartMonth={uploadWalmartMonth}
        setUploadWalmartMonth={setUploadWalmartMonth}
        uploadWalmartYear={uploadWalmartYear}
        setUploadWalmartYear={setUploadWalmartYear}
        walmartPeriodMode={walmartPeriodMode}
        setWalmartPeriodMode={setWalmartPeriodMode}
        handleWalmartFileUpload={handleWalmartFileUpload}
        handleWalmartUploadSubmit={handleWalmartUploadSubmit}
      />

      <EtsyUploadPopup
        showEtsyUploadPopup={showEtsyUploadPopup}
        setShowEtsyUploadPopup={setShowEtsyUploadPopup}
        etsyFileInputRef={etsyFileInputRef}
        selectedEtsyFile={selectedEtsyFile}
        setSelectedEtsyFile={setSelectedEtsyFile}
        parsedEtsyData={parsedEtsyData}
        setParsedEtsyData={setParsedEtsyData}
        uploadEtsyStatus={uploadEtsyStatus}
        setUploadEtsyStatus={setUploadEtsyStatus}
        uploadEtsyWeek={uploadEtsyWeek}
        setUploadEtsyWeek={setUploadEtsyWeek}
        uploadEtsyMonth={uploadEtsyMonth}
        setUploadEtsyMonth={setUploadEtsyMonth}
        uploadEtsyYear={uploadEtsyYear}
        setUploadEtsyYear={setUploadEtsyYear}
        etsyPeriodMode={etsyPeriodMode}
        setEtsyPeriodMode={setEtsyPeriodMode}
        handleEtsyFileUpload={handleEtsyFileUpload}
        handleEtsyUploadSubmit={handleEtsyUploadSubmit}
      />

      <ShopifyUploadPopup
        showShopifyUploadPopup={showShopifyUploadPopup}
        setShowShopifyUploadPopup={setShowShopifyUploadPopup}
        uploadShopifyWeek={uploadShopifyWeek}
        setUploadShopifyWeek={setUploadShopifyWeek}
        uploadShopifyMonth={uploadShopifyMonth}
        setUploadShopifyMonth={setUploadShopifyMonth}
        uploadShopifyYear={uploadShopifyYear}
        setUploadShopifyYear={setUploadShopifyYear}
        howManyWeeks={howManyWeeks}
        setHowManyWeeks={setHowManyWeeks}
        uploadMode={uploadMode}
        setUploadMode={setUploadMode}
        shopifyPeriodMode={shopifyPeriodMode}
        setShopifyPeriodMode={setShopifyPeriodMode}
        parsedShopifyData={parsedShopifyData}
        setParsedShopifyData={setParsedShopifyData}
        uploadShopifyStatus={uploadShopifyStatus}
        setUploadShopifyStatus={setUploadShopifyStatus}
        isLoadingPreview={isLoadingPreview}
        isLoadingUpload={isLoadingUpload}
        loadShopifyPreview={loadShopifyPreview}
        handleShopifyUploadSubmit={handleShopifyUploadSubmit}
        closeShopifyUploadPopup={closeShopifyUploadPopup}
      />

      <SeasonalityCoefficientsPopup
        marketplaceName={marketplaceConfig?.name || 'Amazon'}
        isOpen={seasonality.isPopupOpen}
        onClose={seasonality.closePopup}
        loading={seasonality.loading}
        saving={seasonality.saving}
        error={seasonality.error}
        successMessage={seasonality.successMessage}
        niches={seasonality.niches}
        updateCoefficient={seasonality.updateCoefficient}
        applyBulkCoefficients={seasonality.applyBulkCoefficients}
        addNiche={seasonality.addNiche}
        saveChanges={seasonality.saveChanges}
        cancelChanges={seasonality.cancelChanges}
      />

      <WalmartStockUploadPopup
        showWalmartStockPopup={walmartStock.showWalmartStockPopup}
        setShowWalmartStockPopup={walmartStock.setShowWalmartStockPopup}
        selectedFile={walmartStock.selectedFile}
        setSelectedFile={walmartStock.setSelectedFile}
        parsedData={walmartStock.parsedData}
        setParsedData={walmartStock.setParsedData}
        uploadStatus={walmartStock.uploadStatus}
        setUploadStatus={walmartStock.setUploadStatus}
        fileInputRef={walmartStock.fileInputRef}
        handleFileUpload={walmartStock.handleFileUpload}
        handleUploadSubmit={walmartStock.handleUploadSubmit}
        closePopup={walmartStock.closePopup}
      />
            
      {/* Разворачиваемый блок с фильтрами отображения */}
      {showFilters && (
        <div className="filters-expanded">
          <div className="filter-grid">
            {/* Фильтр по брендам, и по нишам, и по типу товара */}
            <div className="filter-section">
              <h3 className="filter-title">Filters</h3>
              
              {/* Фильтр по брендам */}
              <div className="filter-group">
                <div className="filter-group-header">
                  <h4 className="filter-subtitle">Brands</h4>
                  {selectedBrands.length > 0 && (
                    <button className="clear-filter-btn" onClick={clearBrandFilter}>
                      Clear
                    </button>
                  )}
                </div>
                <div className="filter-checkboxes">
                  {brandsData.map(brand => (
                    <label key={brand.brandID} className="filter-checkbox">
                      <input
                        type="checkbox"
                        checked={selectedBrands.includes(brand.brandID)}
                        onChange={() => handleBrandFilterChange(brand.brandID)}
                      />
                      <span className="checkbox-label">
                        {brand.brandName} ({brand.brandID})
                      </span>
                    </label>
                  ))}
                </div>
              </div>
              
              {/* Фильтр по нишам */}
              <div className="filter-group">
                <div className="filter-group-header">
                  <h4 className="filter-subtitle">Niches</h4>
                  {selectedNiches.length > 0 && (
                    <button className="clear-filter-btn" onClick={clearNicheFilter}>
                      Clear
                    </button>
                  )}
                </div>
                <div className="filter-checkboxes">
                  {nichesData.map(niche => (
                    <label key={niche.niche_category_id} className="filter-checkbox">
                      <input
                        type="checkbox"
                        checked={selectedNiches.includes(niche.niche_category_id.toString())}
                        onChange={() => handleNicheFilterChange(niche.niche_category_id.toString())}
                      />
                      <span className="checkbox-label">
                        {niche.niche_name}
                      </span>
                    </label>
                  ))}
                  {/* Опция для продуктов без ниши (individualpackets) */}
                  <label className="filter-checkbox">
                    <input
                      type="checkbox"
                      checked={selectedNiches.includes('none')}
                      onChange={() => handleNicheFilterChange('none')}
                    />
                    <span className="checkbox-label">
                      No Niche (Individual Packets)
                    </span>
                  </label>
                </div>
              </div>
              
              {/* Фильтр по типам товаров */}
              <div className="filter-group">
                <div className="filter-group-header">
                  <h4 className="filter-subtitle">Product Types</h4>
                  {selectedProductTypes.length > 0 && (
                    <button className="clear-filter-btn" onClick={clearProductTypeFilter}>
                      Clear
                    </button>
                  )}
                </div>
                <div className="filter-checkboxes">
                  <label className="filter-checkbox">
                    <input
                      type="checkbox"
                      checked={selectedProductTypes.includes('sets')}
                      onChange={() => handleProductTypeFilterChange('sets')}
                    />
                    <span className="checkbox-label">
                      Sets
                    </span>
                  </label>
                  <label className="filter-checkbox">
                    <input
                      type="checkbox"
                      checked={selectedProductTypes.includes('wildmicr')}
                      onChange={() => handleProductTypeFilterChange('wildmicr')}
                    />
                    <span className="checkbox-label">
                      Microgreens & Wildflowers
                    </span>
                  </label>
                  <label className="filter-checkbox">
                    <input
                      type="checkbox"
                      checked={selectedProductTypes.includes('individualpackets')}
                      onChange={() => handleProductTypeFilterChange('individualpackets')}
                    />
                    <span className="checkbox-label">
                      Individual Packets
                    </span>
                  </label>
                  <label className="filter-checkbox">
                    <input
                      type="checkbox"
                      checked={selectedProductTypes.includes('kit')}
                      onChange={() => handleProductTypeFilterChange('kit')}
                    />
                    <span className="checkbox-label">
                      Kits
                    </span>
                  </label>
                  <label className="filter-checkbox">
                    <input
                      type="checkbox"
                      checked={selectedProductTypes.includes('fert_product')}
                      onChange={() => handleProductTypeFilterChange('fert_product')}
                    />
                    <span className="checkbox-label">
                      Fertilizers
                    </span>
                  </label>
                  <label className="filter-checkbox">
                    <input
                      type="checkbox"
                      checked={selectedProductTypes.includes('readyproduct')}
                      onChange={() => handleProductTypeFilterChange('readyproduct')}
                    />
                    <span className="checkbox-label">
                      Ready Products
                    </span>
                  </label>
                </div>
              </div>
              
              {/* Кнопка сброса всех фильтров */}
              {((selectedBrands && selectedBrands.length > 0) || 
                (selectedNiches && selectedNiches.length > 0) || 
                (selectedProductTypes && selectedProductTypes.length > 0)) && (
                <div className="filter-actions">
                  <button 
                    className="clear-all-filters-btn"
                    onClick={clearAllFilters}
                  >
                    Clear All Filters
                  </button>
                </div>
              )}
              
              {/* Статистика фильтров */}
               <div className="filter-stats">
                <p>
                  Showing {filteredProducts.length} of {productsData.length} products
                  {((selectedBrands && selectedBrands.length > 0) || 
                    (selectedNiches && selectedNiches.length > 0) || 
                    (selectedProductTypes && selectedProductTypes.length > 0)) && (
                    <span className="active-filters">
                      (with {selectedBrands?.length || 0} brand, {selectedNiches?.length || 0} niche, and {selectedProductTypes?.length || 0} type filters)
                    </span>
                  )}
                </p>
              </div>
            </div>
            
            {/* Фильтр столбцов, управление неделями и предыдущими годами */}
            <div className="filter-section">
              <h3 className="filter-title">Visible Columns</h3>
              <div className="column-checkboxes">
                <label className="column-checkbox">
                  <input
                    type="checkbox"
                    checked={visibleColumns.historical}
                    onChange={() => toggleColumn('historical')}
                  />
                  <span className="checkbox-label">Historical Data</span>
                </label>
                <label className="column-checkbox">
                  <input
                    type="checkbox"
                    checked={visibleColumns.currentWeek}
                    onChange={() => toggleColumn('currentWeek')}
                  />
                  <span className="checkbox-label">Current Week</span>
                </label>
                <label className="column-checkbox">
                  <input
                    type="checkbox"
                    checked={visibleColumns.prediction}
                    onChange={() => toggleColumn('prediction')}
                  />
                  <span className="checkbox-label">Prediction</span>
                </label>
              </div>
              <p className="filter-description">
                Select which data sections to display
              </p>

              {/* Управление видимостью прошлого года */}
              <h3 className="filter-title" style={{ marginTop: '20px' }}>Previous Years</h3>
              <div className="toggle-switch-container">
                <label className="toggle-switch">
                  <input
                    type="checkbox"
                    checked={showAllPreviousYears}
                    onChange={toggleAllPreviousYears}
                  />
                  <span className="toggle-slider"></span>
                </label>
                <span className="toggle-label">
                  {showAllPreviousYears ? 'Hide All Previous Years' : 'Show All Previous Years'}
                </span>
              </div>
              <p className="filter-description">
                Toggle visibility of previous year data for all products
              </p>

              {/* Управление видимостью строки остатков */}
              <h3 className="filter-title" style={{ marginTop: '20px' }}>Stock Balance Row</h3>
              <div className="toggle-switch-container">
                <label className="toggle-switch">
                  <input
                    type="checkbox"
                    checked={showStockBalanceRow}
                    onChange={toggleStockBalanceRow}
                  />
                  <span className="toggle-slider"></span>
                </label>
                <span className="toggle-label">
                  {showStockBalanceRow ? 'Hide Stock Balance Row' : 'Show Stock Balance Row'}
                </span>
              </div>
              <p className="filter-description">
                Show estimated end-of-{viewMode === 'monthly' ? 'month' : 'week'} stock balance (WH + {viewMode === 'monthly' ? 'marketplace stock' : 'Amazon + AWD'} + production − forecast)
              </p>

              {/* Управление видимостью строки прогноза ИИ-агента */}
              <h3 className="filter-title" style={{ marginTop: '20px' }}>Agentic</h3>
              <div className="toggle-switch-container">
                <label className="toggle-switch">
                  <input
                    type="checkbox"
                    checked={showAgenticRow}
                    onChange={toggleAgenticRow}
                  />
                  <span className="toggle-slider"></span>
                </label>
                <span className="toggle-label">
                  {showAgenticRow ? 'Hide AI Forecast Row' : 'Show AI Forecast Row'}
                </span>
              </div>
              <p className="filter-description">
                Show the AI agent forecast under the main forecast row (Amazon only). For past periods the cell compares forecasts with reality: green — AI was closer than our forecast, red — further (hover a cell for AI / our forecast / actual numbers).
              </p>
              {onOpenAiHistory && (
                <button
                  type="button"
                  className="ai-history-btn"
                  onClick={onOpenAiHistory}
                  title="AI forecast versions — view submissions and roll back"
                >
                  AI Forecast History / Rollback
                </button>
              )}
              {/* ── WEEKLY SETTINGS ─────────────────────────── */}
              {viewMode === 'weekly' && (
                <>
                  <h3 className="filter-title" style={{ marginTop: '20px' }}>Historical Weeks Count</h3>
                  <div className="weeks-count-controls">
                    <div className="standard-weeks-buttons">
                      {[4, 8, 12, 24].map(n => (
                        <button
                          key={n}
                          className={`weeks-count-btn ${historicalWeeksCount === n ? 'active' : ''}`}
                          onClick={() => applyStandardHistoricalWeeks(n)}
                        >
                          {n} weeks
                        </button>
                      ))}
                    </div>
                    <div className="custom-weeks-input">
                      <input
                        type="number"
                        min="1"
                        max="52"
                        value={customHistoricalWeeks}
                        onChange={(e) => setCustomHistoricalWeeks(e.target.value)}
                        placeholder="Custom weeks (1-52)"
                        className="custom-weeks-field"
                      />
                      <button
                        className="apply-custom-weeks-btn"
                        onClick={() => applyCustomHistoricalWeeks()}
                        disabled={!customHistoricalWeeks || parseInt(customHistoricalWeeks) < 1 || parseInt(customHistoricalWeeks) > 52}
                      >
                        Apply
                      </button>
                    </div>
                    <div className="current-weeks-info">
                      Currently showing: <strong>{historicalWeeksCount} weeks</strong>
                    </div>
                  </div>
                  <p className="filter-description">Select how many historical weeks to display (1-52 weeks)</p>

                  <h3 className="filter-title" style={{ marginTop: '20px' }}>Forecast Weeks Count</h3>
                  <div className="weeks-count-controls">
                    <div className="standard-weeks-buttons">
                      {[4, 8, 12, 24, 52].map(n => (
                        <button
                          key={n}
                          className={`weeks-count-btn ${forecastWeeksCount === n ? 'active' : ''}`}
                          onClick={() => applyStandardForecastWeeks(n)}
                        >
                          {n} weeks
                        </button>
                      ))}
                    </div>
                    <div className="custom-weeks-input">
                      <input
                        type="number"
                        min="1"
                        max="52"
                        value={customForecastWeeks}
                        onChange={(e) => setCustomForecastWeeks(e.target.value)}
                        placeholder="Custom weeks (1-52)"
                        className="custom-weeks-field"
                      />
                      <button
                        className="apply-custom-weeks-btn"
                        onClick={applyCustomForecastWeeks}
                        disabled={!customForecastWeeks || parseInt(customForecastWeeks) < 1 || parseInt(customForecastWeeks) > 52}
                      >
                        Apply
                      </button>
                    </div>
                    <div className="current-weeks-info">
                      Currently showing: <strong>{forecastWeeksCount} weeks</strong>
                    </div>
                  </div>
                  <p className="filter-description">Select how many forecast weeks to display (1-52 weeks)</p>

                  <h3 className="filter-title" style={{ marginTop: '20px' }}>Flag Median Weeks</h3>
                  <div className="weeks-count-controls">
                    <div className="standard-weeks-buttons">
                      {[2, 4, 6, 8].map(n => (
                        <button
                          key={n}
                          className={`weeks-count-btn ${medianWeeksCount === n ? 'active' : ''}`}
                          onClick={() => setMedianWeeksCount(n)}
                        >
                          {n} weeks
                        </button>
                      ))}
                    </div>
                    <div className="current-weeks-info">
                      Currently: <strong>{medianWeeksCount} clean weeks</strong>
                    </div>
                  </div>
                  <p className="filter-description">
                    Number of previous clean (flag-free) weeks used to compute the median replacement value when a flag is set on a previous-year week.
                  </p>
                </>
              )}

              {/* ── MONTHLY SETTINGS ────────────────────────── */}
              {viewMode === 'monthly' && (
                <>
                  <h3 className="filter-title" style={{ marginTop: '20px' }}>Historical Month Count</h3>
                  <div className="weeks-count-controls">
                    <div className="standard-weeks-buttons">
                      {[2, 3, 6, 12].map(n => (
                        <button
                          key={n}
                          className={`weeks-count-btn ${historicalMonthsCount === n ? 'active' : ''}`}
                          onClick={() => setHistoricalMonthsCount(n)}
                        >
                          {n} mo
                        </button>
                      ))}
                    </div>
                    <div className="current-weeks-info">
                      Currently showing: <strong>{historicalMonthsCount} months</strong>
                    </div>
                  </div>
                  <p className="filter-description">Select how many historical months to display</p>

                  <h3 className="filter-title" style={{ marginTop: '20px' }}>Forecast Month Count</h3>
                  <div className="weeks-count-controls">
                    <div className="standard-weeks-buttons">
                      {[3, 6, 12, 24].map(n => (
                        <button
                          key={n}
                          className={`weeks-count-btn ${forecastMonthsCount === n ? 'active' : ''}`}
                          onClick={() => setForecastMonthsCount(n)}
                        >
                          {n} mo
                        </button>
                      ))}
                    </div>
                    <div className="current-weeks-info">
                      Currently showing: <strong>{forecastMonthsCount} months</strong>
                    </div>
                  </div>
                  <p className="filter-description">Select how many forecast months to display</p>

                  <h3 className="filter-title" style={{ marginTop: '20px' }}>Flag Median Month</h3>
                  <div className="weeks-count-controls">
                    <div className="standard-weeks-buttons">
                      {[1, 2, 3, 6].map(n => (
                        <button
                          key={n}
                          className={`weeks-count-btn ${medianMonthsCount === n ? 'active' : ''}`}
                          onClick={() => setMedianMonthsCount(n)}
                        >
                          {n} mo
                        </button>
                      ))}
                    </div>
                    <div className="current-weeks-info">
                      Currently: <strong>{medianMonthsCount} clean months</strong>
                    </div>
                  </div>
                  <p className="filter-description">
                    Number of previous clean (flag-free) months used to compute the median replacement value when a flag is set on a previous-year month.
                  </p>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default YoyAdminPanel;